import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar';
import { DialogBoxComp } from '../../atoms/DialogBox';
import { LeaveRequestBody, POSTLeaveRequest } from '../../../services/leave';
import { LeaveType } from '../../../utils/entities/type';
// import { getTechnicians } from '../../../services/technicians'; // 🔁 Adjust to your fetch method
import { GetTechnician, GETTechnicians } from '../../../services/technician';
import { ITechnician } from '../../../utils/entities/interface';
import { DateField } from '../../../pages/Booking/Details';
import moment from 'moment';
const leaveTypes: { value: LeaveType; label: string }[] = [
  { value: 'SICK_LEAVE', label: 'Sick Leave' },
  { value: 'CASUAL_LEAVE', label: 'Casual Leave' },
  { value: 'PAID_LEAVE', label: 'Paid Leave' },
];

const AddLeaveRequestForm = ({ onSaved, handleDrawerClose }: any) => {
  const [formData, setFormData] = useState<LeaveRequestBody>({
    technicianId: '',
    startDate: '',
    endDate: '',
    leaveType: 'SICK_LEAVE',
    reason: '',
  });

  const [technicians, setTechnicians] = useState<ITechnician[] | null>(null);
  const [selectedTechnician, setSelectedTechnician] =
    useState<ITechnician | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const obj: any = { limit: 50 };

        if (inputValue && inputValue.trim() !== '') {
          obj.searchQuery = inputValue.trim();
        }
        const list = await GETTechnicians(obj); // Should return array of { _id, name }
        setTechnicians(list.data);
      } catch (err) {
        showSnackbar('Failed to load technicians', 'error');
      }
    };
    fetchTechnicians();
  }, [inputValue]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    console.log('formData', formData);
    if (
      !formData.technicianId ||
      !formData.leaveType ||
      !formData.startDate ||
      !formData.endDate
    )
      return;
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    const payload = {
      ...formData,
      startDate: moment(formData.startDate).format('DD-MM-YYYY'),
      endDate: moment(formData.endDate).format('DD-MM-YYYY'),
    };
    try {
      const leave = await POSTLeaveRequest(payload);

      if (leave) {
        showSnackbar('Leave request submitted!', 'success');
        setTimeout(() => {
          if (onSaved) onSaved(leave);
          handleDrawerClose();
        }, 500);
      } else {
        showSnackbar('Failed to submit leave request.', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.message || 'Error occurred.', 'error');
    } finally {
      setDialogOpen(false);
    }
  };

  const handleReset = () => {
    setSelectedTechnician(null);
    setInputValue('');
    setFormData({
      technicianId: '',
      startDate: '',
      endDate: '',
      leaveType: 'SICK_LEAVE',
      reason: '',
    });
  };

  return (
    <Box sx={{ padding: 0, flex: 1 }}>
      {SnackbarComponent}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1">Technician</Typography>
        {/* <TextField
          select
          name="technicianId"
          value={formData.technicianId}
          onChange={handleChange}
          required
        >
          {Array.isArray(technicians) &&
            technicians.map((tech) => (
              <MenuItem key={tech._id} value={tech._id}>
                {tech.name}
              </MenuItem>
            ))}
        </TextField> */}

        <Autocomplete
          options={Array.isArray(technicians) ? technicians : []}
          getOptionLabel={(option) =>
            `${option.name || 'N/A'} (${option.phoneNumber.slice(0, 10)})`
          }
          value={selectedTechnician}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          onChange={(event, newValue) => {
            setSelectedTechnician(newValue);
            setFormData((prev) => ({
              ...prev,
              technicianId: newValue?._id || '',
            }));
          }}
          onInputChange={(event, newInputValue, reason) => {
            // Only refetch when the user actually types; 'reset' fires on
            // selection and would trigger a fetch/re-render loop.
            if (reason === 'input') {
              setInputValue(newInputValue);
            }
          }}
          sx={{
            flex: 1,
            '& .MuiAutocomplete-listbox': {
              '&::-webkit-scrollbar': {
                display: 'none', // Hides scrollbar for webkit browsers
              },
              '&::-moz-scrollbar': {
                display: 'none', // Hides scrollbar for Firefox
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select User"
              sx={{
                input: { color: '#8A8D91' },
                label: { color: '#8A8D91' },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: <>{params.InputProps.endAdornment}</>,
              }}
            />
          )}
        />

        <Typography variant="subtitle1">Leave Type</Typography>
        <TextField
          select
          name="leaveType"
          value={formData.leaveType}
          onChange={handleChange}
          required
        >
          {leaveTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">Start Date</Typography>
            <DateField
              fullWidth
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">End Date</Typography>
            <DateField
              fullWidth
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Typography variant="subtitle1">Reason (Optional)</Typography>
        <TextField
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          multiline
          minRows={3}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Box>

      <DialogBoxComp
        open={dialogOpen}
        title="Confirm Leave Request"
        content="Are you sure you want to submit this leave request?"
        handleClose={() => setDialogOpen(false)}
        handleConfirm={handleConfirm}
      />
    </Box>
  );
};

export default AddLeaveRequestForm;
